import { type OperacionResponse } from '../lib/api.js';
import { fmtBs, fmtInt, fmtPct } from '../lib/format.js';
import { ChartCard } from './charts/ChartCard.js';
import { KpiCard } from './KpiCard.js';

/**
 * Movimiento diario del Manager-Flash y el PACE a futuro.
 *
 * Todo esto lo guardaba `registro_diario` desde el primer día sin que nadie lo
 * leyera. El pace es lo ÚNICO de la app que mira hacia adelante: el resto del
 * tablero es histórico.
 */
export function OperacionPanel({ data }: { data: OperacionResponse }) {
  if (data.sinDatos) {
    return (
      <ChartCard title="Movimiento y pace">
        <p className="p-4 text-sm text-slate-500">
          Sin movimiento cargado en el rango. Sale del Manager-Flash: walk-in, day use, no-show,
          salidas anticipadas, reservas hechas y canceladas, y la ocupación ya vendida a futuro.
        </p>
      </ChartCard>
    );
  }

  const m = data.movimiento;
  const filas: { label: string; valor: number; nota?: string }[] = [
    { label: 'Walk-in', valor: m.walkin, nota: 'Llegadas sin reserva previa' },
    { label: 'Day use', valor: m.dayUse, nota: 'Habitaciones usadas sin pernocta' },
    { label: 'No-show', valor: m.noShow, nota: 'Reservas que no se presentaron' },
    { label: 'Salidas anticipadas', valor: m.earlyDeparture },
    { label: 'Estancias extendidas', valor: m.extendedStay },
    { label: 'Reservas hechas', valor: m.reservationsMade },
    { label: 'Reservas canceladas', valor: m.cancellations },
  ];

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
        Movimiento y pace · {data.from} a {data.to}
      </h2>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Walk-in" kpi={data.cards.walkin} format={fmtInt} />
        <KpiCard label="No-show" kpi={data.cards.noShow} format={fmtInt} />
        <KpiCard label="Reservas hechas" kpi={data.cards.reservationsMade} format={fmtInt} />
        <KpiCard label="Reservas canceladas" kpi={data.cards.cancellations} format={fmtInt} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title={`Movimiento del período (${data.dias} día/s con dato)`}>
          <table className="w-full text-sm">
            <tbody>
              {filas.map((f) => (
                <tr key={f.label} className="border-t border-slate-700/60 first:border-0">
                  <td className="px-3 py-1.5 text-slate-200">
                    {f.label}
                    {f.nota && <span className="block text-[11px] text-slate-500">{f.nota}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                    {fmtInt(f.valor)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-slate-700">
                <td className="px-3 py-1.5 text-slate-200">
                  Tarifa media por persona
                  <span className="block text-[11px] text-slate-500">
                    Recalculada sobre el período, no es el promedio de las medias diarias
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-200">
                  {data.avgPersonRateBs == null ? '—' : fmtBs(data.avgPersonRateBs)}
                </td>
              </tr>
            </tbody>
          </table>
        </ChartCard>

        {/* El pace no se acumula: es una foto. Se rotula SU fecha para que no se
            lea como si fuera del rango entero. */}
        <ChartCard
          title={
            data.pace.fecha
              ? `Pace · foto del ${data.pace.fecha} (${data.pace.sedes} sede/s)`
              : 'Pace'
          }
        >
          {data.pace.fecha == null ? (
            <p className="p-4 text-sm text-slate-500">
              El flash del rango no trae las columnas de ocupación a futuro.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 p-3">
                {(
                  [
                    ['Próximos 7 días', data.pace.next7],
                    ['Próximos 31 días', data.pace.next31],
                    ['Próximos 365 días', data.pace.next365],
                  ] as const
                ).map(([label, v]) => (
                  <div key={label} className="rounded-lg border border-slate-700 bg-slate-900 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums text-slate-100">
                      {v == null ? '—' : fmtPct(v * 100)}
                    </p>
                    <div className="mt-2 h-1.5 w-full rounded bg-slate-800">
                      <div
                        className="h-1.5 rounded bg-emerald-500"
                        style={{ width: v == null ? '0%' : `${Math.min(v * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="px-3 pb-3 text-[11px] text-slate-500">
                Ocupación YA VENDIDA para los días que vienen, no una proyección. Es una foto del
                día más reciente cargado —no del rango—, y varias sedes se combinan pesando cada una
                por sus habitaciones.
              </p>
            </>
          )}
        </ChartCard>
      </div>
    </section>
  );
}
